import React, { useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { Play, Loader } from 'lucide-react';

const MONACO_THEME = {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '5bb8f5' },
    { token: 'string', foreground: 'ce9178' },
    { token: 'number', foreground: 'd4a843' },
    { token: 'comment', foreground: '666666', fontStyle: 'italic' },
    { token: 'operator', foreground: 'e8884a' },
    { token: 'identifier', foreground: 'e8e8e8' },
  ],
  colors: {
    'editor.background': '#242424',
    'editor.foreground': '#e8e8e8',
    'editorLineNumber.foreground': '#444444',
    'editorLineNumber.activeForeground': '#888888',
    'editor.lineHighlightBackground': '#2d2d2d',
    'editorCursor.foreground': '#5bb8f5',
    'editor.selectionBackground': '#5bb8f520',
    'editorWidget.background': '#2d2d2d',
    'editorSuggestWidget.background': '#2d2d2d',
    'editorSuggestWidget.border': '#383838',
    'editorSuggestWidget.selectedBackground': '#383838',
  },
};

export default function SQLEditor({ value, onChange, onRun, running, tables }) {
  const editorRef = useRef(null);

  const handleMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monaco.editor.defineTheme('macarowni', MONACO_THEME);
    monaco.editor.setTheme('macarowni');

    monaco.languages.registerCompletionItemProvider('sql', {
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };
        const tableCompletions = tables.map(t => ({
          label: t.name,
          kind: monaco.languages.CompletionItemKind.Class,
          insertText: `"${t.name}"`,
          range,
          detail: `${t.rowCount?.toLocaleString()} rows`,
        }));
        const columnCompletions = tables.flatMap(t =>
          t.schema.map(col => ({
            label: col.name,
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: col.name,
            range,
            detail: col.type,
          }))
        );
        return { suggestions: [...tableCompletions, ...columnCompletions] };
      },
    });

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
      onRun();
    });
  }, [tables, onRun]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 12px', background: 'var(--bg2)',
        borderBottom: '1px solid var(--border)', flexShrink: 0,
      }}>
        <span style={{ color: 'var(--text2)', fontSize: 12 }}>SQL</span>
        <span style={{ color: 'var(--border2)' }}>·</span>
        <span style={{ color: 'var(--text3)', fontSize: 11 }}>⌘↵ to run</span>
        <div style={{ flex: 1 }} />
        <button
          onClick={onRun}
          disabled={running}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 16px', background: running ? 'var(--bg3)' : 'var(--accent)',
            border: 'none', borderRadius: 4,
            color: running ? 'var(--text2)' : '#fff',
            fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600,
            cursor: running ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s',
            textTransform: 'uppercase', letterSpacing: '0.04em',
          }}
          onMouseEnter={e => { if (!running) e.currentTarget.style.background = 'var(--accent2)'; }}
          onMouseLeave={e => { if (!running) e.currentTarget.style.background = running ? 'var(--bg3)' : 'var(--accent)'; }}
        >
          {running
            ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
            : <Play size={12} fill="currentColor" />}
          {running ? 'Running...' : 'Run'}
        </button>
      </div>

      {/* Editor */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Editor
          language="sql"
          value={value}
          onChange={onChange}
          onMount={handleMount}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineHeight: 21,
            fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace",
            fontLigatures: false,
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            padding: { top: 10, bottom: 10 },
            renderLineHighlight: 'line',
            suggestOnTriggerCharacters: true,
            quickSuggestions: true,
            folding: false,
            lineDecorationsWidth: 0,
            overviewRulerLanes: 0,
          }}
        />
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
